import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Alert, TextField, CircularProgress, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import axios from 'axios';

interface ApprovalData {
  id: string;
  driverName: string;
  status: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';
  comments: string | null;
  decidedAt: string | null;
  breakdownSnapshot: any;
  periodStart: string;
  periodEnd: string;
  approverName: string | null;
  orgName: string;
  hasPdf: boolean;
}

const fmt = (n: number, cur = 'DKK') =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: cur }).format(n);

export default function PublicPayrollApproval() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get('action');
  const [data, setData] = useState<ApprovalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'approve' | 'changes'>('view');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`/api/public/payroll-approval/${token}`).then(r => {
      setData(r.data.data);
      if (initialAction === 'approve' && r.data.data.status === 'PENDING') setMode('approve');
      else if (initialAction === 'changes' && r.data.data.status === 'PENDING') setMode('changes');
    }).catch(e => {
      setError(e?.response?.data?.error || 'Linket er ugyldigt eller udløbet');
    }).finally(() => setLoading(false));
  }, [token, initialAction]);

  async function submit(status: 'APPROVED' | 'CHANGES_REQUESTED') {
    if (status === 'CHANGES_REQUESTED' && !comments.trim()) {
      setError('Skriv hvad der skal rettes inden du sender');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`/api/public/payroll-approval/${token}/decide`, { status, comments: comments || null });
      setData(d => d ? { ...d, status: r.data.data.status, comments, decidedAt: r.data.data.decidedAt } : d);
      setMode('view');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunne ikke gemme');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error && !data) return <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return null;

  const b = data.breakdownSnapshot;
  const statusChip =
    data.status === 'APPROVED' ? <Chip icon={<CheckCircleIcon />} color="success" label={`Godkendt ${data.decidedAt ? new Date(data.decidedAt).toLocaleString('da-DK') : ''}`} /> :
    data.status === 'CHANGES_REQUESTED' ? <Chip icon={<EditNoteIcon />} color="warning" label={`Ændringer bedt om ${data.decidedAt ? new Date(data.decidedAt).toLocaleString('da-DK') : ''}`} /> :
    <Chip label="Afventer godkendelse" color="info" />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="caption" color="text.secondary">{data.orgName}</Typography>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Lønoverblik til godkendelse</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {data.driverName} - perioden {data.periodStart} til {data.periodEnd}
      </Typography>
      <Box sx={{ mb: 2 }}>{statusChip}</Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Totaler</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.5, fontSize: 14 }}>
            <span>Grundløn ({(b.totalNormalMin / 60).toFixed(1)} t)</span><strong>{fmt(b.baseTotal || 0)}</strong>
            <span>50% overtid ({(b.totalOt1Min / 60).toFixed(1)} t)</span><strong>{fmt(b.ot1Total || 0)}</strong>
            <span>100% overtid ({(b.totalOt2Min / 60).toFixed(1)} t)</span><strong>{fmt(b.ot2Total || 0)}</strong>
            <span>Aften-tillæg</span><strong>{fmt(b.eveTotal || 0)}</strong>
            <span>Nat-tillæg</span><strong>{fmt(b.nightTotal || 0)}</strong>
            <span style={{ borderTop: '1px solid #ccc', paddingTop: 4, marginTop: 4 }}><strong>Brutto</strong></span>
            <strong style={{ borderTop: '1px solid #ccc', paddingTop: 4, marginTop: 4 }}>{fmt(b.gross || 0)}</strong>
          </Box>
        </CardContent>
      </Card>

      {data.hasPdf && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            href={`/api/public/payroll-approval/${token}/pdf`}
            target="_blank"
          >
            Åbn detaljeret PDF i ny fane
          </Button>
        </Box>
      )}

      {data.status !== 'PENDING' ? (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600}>Din beslutning</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {data.status === 'APPROVED' ? 'Du har godkendt dette lønoverblik.' : 'Du har bedt om ændringer.'}
            </Typography>
            {data.comments && (
              <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Bemærkninger:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.comments}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : mode === 'view' ? (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" size="large" startIcon={<CheckCircleIcon />} onClick={() => setMode('approve')}>
            Godkend lønoverblik
          </Button>
          <Button variant="outlined" color="warning" size="large" startIcon={<EditNoteIcon />} onClick={() => setMode('changes')}>
            Bed om ændringer
          </Button>
        </Box>
      ) : mode === 'approve' ? (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Bekræft godkendelse</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Du er ved at godkende lønoverblikket for {data.driverName} for perioden {data.periodStart} til {data.periodEnd}.
            </Typography>
            <TextField
              fullWidth multiline minRows={2} size="small"
              placeholder="Valgfri bemærkning (fx 'Set og godkendt')"
              value={comments} onChange={e => setComments(e.target.value)} sx={{ mb: 2 }}
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="success" disabled={submitting} onClick={() => submit('APPROVED')}>
                {submitting ? <CircularProgress size={20} /> : 'Bekræft godkendelse'}
              </Button>
              <Button onClick={() => setMode('view')}>Annuller</Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Bed om ændringer</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Skriv hvad der skal rettes så administrator kan justere lønoverblikket.
            </Typography>
            <TextField
              fullWidth multiline minRows={4} size="small" autoFocus
              placeholder="Fx 'Bo's torsdag 14/6 er sat til 8 timer men han var ikke på arbejde'"
              value={comments} onChange={e => setComments(e.target.value)} sx={{ mb: 2 }}
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="warning" disabled={submitting || !comments.trim()} onClick={() => submit('CHANGES_REQUESTED')}>
                {submitting ? <CircularProgress size={20} /> : 'Send bemærkninger'}
              </Button>
              <Button onClick={() => setMode('view')}>Annuller</Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
